/**
 * Support Inbox — Worklist.
 *
 * What is left to do on the supplier side, one card per ORDER (the sales
 * worklist groups per company; support work is about orders). A card gathers:
 *   - the order's open support inbox emails — a supplier proof (BAT) waiting
 *     for approval is flagged, it blocks the order;
 *   - its status, when that status is flagged "support action required"
 *     (Admin → Order statuses);
 *   - a delay: the expected shipping date has passed and the order has no
 *     tracking yet — a supplier to chase even when nobody wrote.
 * An open support email tied to no order gets a card of its own.
 *
 * Same look as the sales worklist (it borrows its stylesheet through the
 * sales-inbox-worklist-view class). The card header opens the order, an email
 * row opens the email in a window, so the board is never left.
 */
define('supportWorklistView',[
  'require',
  'module',
  'app',
  'marionette',
  'underscore',
  'jquery',
  'settings',
  'salesInboxWorklistRowActions',
  'salesInboxLiveUpdates',
  'entities/sales_inbox'
], function (require, Module, App, Marionette, _, $, Settings, RowActions, LiveUpdates) {
  'use strict';

  var FILTER_STORAGE_KEY = 'supportInbox.worklistFilters';
  var PAGE_SIZE = 25;

  // Search scope: opened (default) | all (resolved mails too) | extended (and the mail content).
  function openScopeOf(value) {
    return value === 'all' || value === 'extended' ? value : 'opened';
  }
  var LIVE_INBOX = 'SUPPORT';
  var LIVE_RELOAD_MIN_MS = 30000;
  var SCORE_FULL = 30;
  var UNASSIGNED = '__none__';

  // The tabs narrow the board to cards having that kind of work; their numbers
  // come back with every page (emailBlocks, proofBlocks…).
  var KINDS = [
    { key: '', label: 'All', count: 'total' },
    { key: 'PROOF', label: 'Proofs to approve', count: 'proofBlocks' },
    { key: 'LATE', label: 'Late', count: 'lateBlocks' },
    { key: 'CHECK', label: 'Need check', count: 'checkBlocks' },
    { key: 'STATUS', label: 'Status to handle', count: 'statusBlocks' },
    { key: 'EMAIL', label: 'Emails', count: 'emailBlocks' },
    // Emails we already answered (waiting on the supplier): left out of every
    // other tab, gathered here.
    { key: 'AWAITING', label: 'Awaiting', count: 'awaitingBlocks' }
  ];

  // Email types of the support inbox, grouped by category (mirrors the
  // backend EmailCase enum). A whole category is one choice of its own
  // ("cat:" prefix), expanded to its cases on the way to the backend.
  var TYPE_GROUPS = [
    { key: 'SUPPLIER_ORDER', label: 'Supplier order', cases: ['ORDER_CONFIRMED', 'ORDER_DELAYED', 'OUT_OF_STOCK', 'SUPPLIER_QUESTION'] },
    { key: 'SUPPLIER_SHIPPING', label: 'Supplier shipping', cases: ['SHIPPED', 'SHIPPING_ISSUE'] },
    { key: 'SUPPLIER_PROOF', label: 'Supplier proof', cases: ['PROOF_TO_VALIDATE', 'ARTWORK_ISSUE'] },
    { key: 'SUPPLIER_INVOICE', label: 'Supplier invoice', cases: ['INVOICE_RECEIVED', 'PAYMENT_REMINDER', 'CREDIT_NOTE'] },
    { key: 'SUPPLIER_CLAIM', label: 'Supplier claim', cases: ['CLAIM_UPDATE'] },
    { key: 'TRACKING', label: 'Tracking', cases: ['WHEN_WILL_ARRIVE', 'DID_NOT_ARRIVE', 'PROBLEM'] },
    { key: 'RECLAMATION', label: 'Reclamation', cases: ['PRINTING_ISSUE', 'BROKEN_GOODS', 'MISSING_GOODS'] },
    { key: 'INVOICING', label: 'Invoicing', cases: ['ASK_FOR_INVOICE', 'PAY_AFTER_DELIVERY', 'CHANGE_DATA', 'ASK_FOR_RIB', 'PAYMENT_RECEIVED'] },
    // Normally resolved at classification; still here for the ones an agent
    // reopened or reclassified by hand.
    { key: 'NOT_ACTIONABLE', label: 'Not actionable', cases: ['OUT_OF_OFFICE', 'EMAIL_NOT_DELIVERED', 'CARRIER_SURVEY', 'VENDOR_SOLICITATION'] }
  ];
  var CATEGORY_PREFIX = 'cat:';

  function typeCases(type) {
    if (!type) { return []; }
    if (type.indexOf(CATEGORY_PREFIX) !== 0) { return [type]; }
    var group = _.findWhere(TYPE_GROUPS, { key: type.slice(CATEGORY_PREFIX.length) });
    return group ? group.cases : [];
  }

  var SORTS = [
    { key: 'priority', label: 'Priority' },
    { key: 'ship', label: 'Ship date' },
    // Soonest reply deadline first (mails without one at the end).
    { key: 'replyDue', label: 'Reply by' },
    { key: 'recent', label: 'Recent' }
  ];

  // The sort a click on a sort tab asks for: "Recent" again flips its direction.
  function nextSort(clicked, current) {
    var key = clicked || 'priority';
    if (key === 'recent' && (current === 'recent' || current === 'oldest')) {
      return current === 'recent' ? 'oldest' : 'recent';
    }
    return key;
  }

  function renderSortTabs($tabs, sort) {
    $tabs.each(function () {
      var key = $(this).attr('data-sort');
      $(this).toggleClass('on', key === sort || (key === 'recent' && sort === 'oldest'));
      $(this).find('.wl-sort-dir').text(key !== 'recent' ? '' :
        (sort === 'oldest' ? ' ↑' : (sort === 'recent' ? ' ↓' : '')));
    });
  }

  var WHO = [
    { key: '', label: 'Everyone' },
    { key: 'mine', label: 'Mine' },
    { key: 'unassigned', label: 'Nobody' },
    { key: 'mineAndUnassigned', label: 'Mine + nobody' }
  ];

  // Same tag colours as the sales worklist, per supplier category.
  var CATEGORY_TONE = {
    SUPPLIER_ORDER: 'design',
    SUPPLIER_SHIPPING: 'tracking',
    SUPPLIER_PROOF: 'sales',
    SUPPLIER_INVOICE: 'invoicing',
    SUPPLIER_CLAIM: 'claim',
    RECLAMATION: 'claim',
    TRACKING: 'tracking',
    INVOICING: 'invoicing'
  };

  var STATUS_LABEL = { NEW: 'New', AWAITING: 'Awaiting' };

  function prettyCase(s) {
    if (!s) { return ''; }
    return String(s).replace(/_/g, ' ').toLowerCase()
      .replace(/\b([a-z])/g, function (m, c) { return c.toUpperCase(); });
  }

  function currentUserSecId() {
    var user = Settings.get('currentUser');
    return user ? (user.secId || null) : null;
  }

  function loadFilters() {
    try {
      var raw = window.localStorage && window.localStorage.getItem(FILTER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }

  function saveFilters(filters) {
    try {
      if (window.localStorage) {
        window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters || {}));
      }
    } catch (e) { /* ignore quota / privacy-mode errors */ }
  }

  function formatDay(value) {
    if (!value) { return ''; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return ''; }
    return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' });
  }

  function ageLabel(value) {
    if (!value) { return ''; }
    var h = (Date.now() - new Date(value).getTime()) / 36e5;
    if (isNaN(h)) { return ''; }
    if (h < 1) { return Math.max(1, Math.round(h * 60)) + 'm'; }
    if (h < 24) { return Math.round(h) + 'h'; }
    return Math.floor(h / 24) + 'd';
  }

  function gaugeFill(score) {
    return Math.max(6, Math.min(100, Math.round((score || 0) / SCORE_FULL * 100)));
  }

  function gaugeHeat(score) {
    if (score >= 20) { return 'hot'; }
    if (score >= 10) { return 'warm'; }
    return 'cool';
  }

  // Waiting pill: quiet while fresh, amber past two days, red past a week.
  function waitPill(block) {
    var days = block.waitingDays;
    if (days === null || days === undefined) { return { tone: 'ok', text: '—', title: 'Nothing dated' }; }
    return {
      tone: days >= 7 ? 'bad' : days >= 2 ? 'warn' : 'ok',
      text: days + 'd',
      title: 'Oldest open item since ' + formatDay(block.waitingSince)
    };
  }

  // Ship pill: the expected shipping date, red once it has passed without a
  // tracking.
  function shipPill(block) {
    if (!block.expShippingDate) { return { tone: 'none', text: '—', title: 'No expected shipping date' }; }
    if (block.late) {
      return { tone: 'bad', text: formatDay(block.expShippingDate),
        title: 'Expected to ship ' + formatDay(block.expShippingDate) + ' — ' + block.daysLate + ' day(s) late, no tracking' };
    }
    return { tone: 'ok', text: formatDay(block.expShippingDate), title: 'Expected to ship ' + formatDay(block.expShippingDate) };
  }

  // Reply deadline read out of the email: "14:00" today, "29/09 14:00"
  // another day; `over` once it has passed.
  function dueTag(value) {
    if (!value) { return null; }
    var d = new Date(value);
    if (isNaN(d.getTime())) { return null; }
    var time = d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });
    var text = d.toDateString() === new Date().toDateString() ? time :
      d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' }) + ' ' + time;
    return { text: 'reply by ' + text, over: d.getTime() <= Date.now() };
  }

  var ROW_TPL = _.template(
    '<li class="wl-row" tabindex="0" role="button" data-type="<%= type %>" data-secid="<%= secId %>" data-poi="<%= poi %>">' +
    '  <span class="wl-ic <%= icTone %>" title="<%= icTitle %>"><i class="fa <%= icon %>"></i></span>' +
    '  <span class="wl-ti">' +
    '    <span class="wl-ti-t" title="<%= title %>"><%= title %></span>' +
    '    <% if (summary) { %><span class="wl-note sw-summary" title="<%= summary %>"><i class="fa fa-magic"></i><%= summary %></span><% } %>' +
    // The agent's latest note on the mail, as on the sales worklist.
    '    <% if (note) { %><span class="wl-note" title="<%= note %>"><i class="fa fa-sticky-note-o"></i><%= note %></span><% } %>' +
    '  </span>' +
    '  <span class="wl-tags">' +
    // An email's status is a button: New / Awaiting / Resolved from the board.
    '    <% if (statusChip) { %><%= statusChip %><% } else if (status) { %><span class="wl-status <%= statusTone %>"><%= status %></span><% } %>' +
    '    <% if (due) { %><span class="wl-tag due<%= dueOver ? " over" : "" %>" title="Reply deadline written in the email"><%= due %></span><% } %>' +
    '    <% if (klass) { %><span class="wl-tag <%= klassTone %>" title="<%= klassTitle %>"><%= klass %></span><% } %>' +
    '  </span>' +
    '  <span class="wl-me"><%= meta %></span>' +
    '  <% if (checkable) { %><button type="button" class="sw-checked <%= checkClass %>" title="<%= checkTitle %>">' +
    '    <i class="fa fa-check"></i> Checked</button><% } %>' +
    // Avatars, as on the sales worklist: the email's assignee (violet ring),
    // then the order's users (dashed) — on an order row, the order's users.
    '  <% if (assignButtons) { %><%= assignButtons %><% } %>' +
    '  <span class="wl-users" data-row="<%= rowKey %>">' +
    '    <span class="wl-users-own js-users-own" title="<%= ownTitle %>"></span>' +
    '    <span class="wl-users-cust js-users-cust" title="Assigned to the order"></span>' +
    '  </span>' +
    '  <span class="wl-rg" title="<%= dateAttr %>"><%= age %></span>' +
    '</li>'
  );

  var BLOCK_TPL = _.template(
    '<section class="wl-card" data-key="<%= blockKey %>" data-poi="<%= poi %>">' +
    '  <header class="wl-head" tabindex="0" role="button" title="<%= headTitle %>">' +
    '    <span class="wl-gauge <%= heat %>" style="--p:<%= fill %>" title="<%= priorityReason %>">' +
    '      <i><%= priority %></i>' +
    '    </span>' +
    '    <span class="wl-av <%= avTone %>"><i class="fa <%= avIcon %>"></i></span>' +
    '    <span class="wl-who">' +
    '      <span class="wl-nm"><span class="wl-nm-t"><%= name %></span></span>' +
    '      <span class="wl-sb"><%= sub %></span>' +
    '    </span>' +
    '    <span class="wl-meta">' +
    '      <span class="wl-pill wait <%= waitTone %>" title="<%= waitTitle %>">' +
    '        <i class="fa fa-hourglass-half"></i><em>waiting</em><b><%= waitText %></b></span>' +
    '      <span class="wl-pill wait <%= shipTone %>" title="<%= shipTitle %>">' +
    '        <i class="fa fa-truck"></i><em>ships</em><b><%= shipText %></b></span>' +
    '    </span>' +
    '    <span class="wl-chips">' +
    '      <% if (proof) { %><span class="wl-chip sw-proof">proof</span><% } %>' +
    '      <% if (late) { %><span class="wl-chip sw-late"><%= daysLate %>d late</span><% } %>' +
    '      <% if (statusAction) { %><span class="wl-chip sw-status"><%= statusName %></span><% } %>' +
    '      <% if (needCheck) { %><span class="wl-chip sw-check"><i class="fa fa-clock-o"></i> check</span><% } %>' +
    '      <% if (emailCount) { %><span class="wl-chip graph"><%= emailCount %> mail<%= emailCount > 1 ? "s" : "" %></span><% } %>' +
    '    </span>' +
    '  </header>' +
    '  <ul class="wl-rows"><%= rows %></ul>' +
    '</section>'
  );

  Module.exports = Marionette.ItemView.extend({
    // Borrows the sales worklist's stylesheet (tokens, cards, rows).
    className: 'sales-inbox-worklist-view support-worklist-view',

    template: _.template(
      '<div class="wl-bar">' +
      '  <% _.each(kinds, function (k) { %>' +
      '  <button type="button" class="wl-tab js-kind <%= k.key === kind ? "on" : "" %>" data-kind="<%= k.key %>" data-count="<%= k.count %>">' +
      '    <%= k.label %><span class="n"></span>' +
      '  </button>' +
      '  <% }); %>' +
      '  <span class="wl-sep"></span>' +
      '  <% _.each(sorts, function (s) { %>' +
      // "Recent" sorts both ways: a second click flips newest-first (↓) to oldest-first (↑, sort=oldest).
      '  <button type="button" class="wl-tab quiet js-sort <%= (s.key === sort || (s.key === "recent" && sort === "oldest")) ? "on" : "" %>" data-sort="<%= s.key %>"' +
      '    <% if (s.key === "recent") { %> title="Click again to flip: newest first ↓ / oldest first ↑"<% } %>>' +
      '<%= s.label %><% if (s.key === "recent") { %><span class="wl-sort-dir"><%= sort === "oldest" ? " ↑" : (sort === "recent" ? " ↓" : "") %></span><% } %></button>' +
      '  <% }); %>' +
      '  <span class="wl-lang">' +
      '    <select class="js-who" title="Whose emails">' +
      '      <% _.each(whos, function (w) { %>' +
      '      <option value="<%= w.key %>" <%= w.key === who ? "selected" : "" %>><%= w.label %></option>' +
      '      <% }); %>' +
      '    </select>' +
      '  </span>' +
      '  <span class="wl-lang">' +
      '    <select class="js-type" title="Email type">' +
      '      <option value="">All types</option>' +
      '      <% _.each(typeGroups, function (g) { %>' +
      '      <optgroup label="<%= g.label %>">' +
      '        <option value="cat:<%= g.key %>" <%= type === "cat:" + g.key ? "selected" : "" %>><%= g.label %> — all</option>' +
      '        <% _.each(g.cases, function (c) { %>' +
      '        <option value="<%= c %>" <%= type === c ? "selected" : "" %>><%= pretty(c) %></option>' +
      '        <% }); %>' +
      '      </optgroup>' +
      '      <% }); %>' +
      '    </select>' +
      '  </span>' +
      '  <span class="wl-grow"></span>' +
      // Opened (default) / All: with All, the search also finds resolved mails;
      // Extended: like All, and it also looks in the mail content (Gmail, slower).
      '  <span class="sw-open-switch" title="All: the search also finds resolved emails. Extended: and looks in the mail content too">' +
      '    <button type="button" class="wl-tab quiet js-open-scope <%= openScope === "opened" ? "on" : "" %>" data-open="opened">Opened</button>' +
      '    <button type="button" class="wl-tab quiet js-open-scope <%= openScope === "all" ? "on" : "" %>" data-open="all">All</button>' +
      '    <button type="button" class="wl-tab quiet js-open-scope <%= openScope === "extended" ? "on" : "" %>" data-open="extended" title="Also in the mail content and attachment names (slower)">Extended</button>' +
      '  </span>' +
      '  <span class="wl-find"><input type="text" class="js-search" placeholder="Order, product, customer, supplier" value="<%= search %>"/></span>' +
      '  <button type="button" class="wl-reload js-refresh" title="Refresh">⟲</button>' +
      '</div>' +
      '<div class="wl-body js-body"></div>' +
      '<div class="wl-footer js-footer"></div>'
    ),

    ui: {
      body: '.js-body',
      footer: '.js-footer',
      search: '.js-search'
    },

    events: {
      'click .js-kind': 'onKind',
      'click .js-sort': 'onSort',
      'change .js-who': 'onWho',
      'change .js-type': 'onType',
      'click .js-open-scope': 'onOpenScope',
      'input .js-search': 'onSearch',
      'click .js-refresh': 'onRefresh',
      'click .js-wl-status': 'onRowStatus',
      'click .js-wl-assign-me': 'onRowAssignMe',
      'click .js-wl-assign': 'onRowAssign',
      'click .js-more': 'onMore',
      'click .js-checked': 'onChecked',
      'click .js-late-checked': 'onLateChecked',
      'click .wl-row': 'onRowClick',
      'keydown .wl-row': 'onRowKey',
      'click .wl-head': 'onHeadClick',
      'keydown .wl-head': 'onHeadKey'
    },

    initialize: function () {
      var saved = loadFilters();
      this.filters = {
        kind: saved.kind || '',
        sort: saved.sort || 'priority',
        who: saved.who || '',
        type: saved.type || '',
        openScope: openScopeOf(saved.openScope),
        search: saved.search || ''
      };
      this.blocks = [];
      this.itemsByKey = {};
      this.userViews = [];
      this.total = 0;
      this.counts = {};
    },

    serializeData: function () {
      return {
        kinds: KINDS,
        kind: this.filters.kind,
        sorts: SORTS,
        sort: this.filters.sort,
        whos: WHO,
        who: this.filters.who,
        openScope: this.filters.openScope,
        typeGroups: TYPE_GROUPS,
        type: this.filters.type,
        pretty: prettyCase,
        search: _.escape(this.filters.search)
      };
    },

    onShow: function () {
      this.fetch(false);
      LiveUpdates.watch(this, this.onItemsChanged);
    },

    // ---- live updates ------------------------------------------------------
    // Inbox items changed somewhere (pushed by the backend). A change to an
    // email on screen reloads the board in place — same cards loaded, same
    // scroll, no "Loading…". A change elsewhere in this inbox may add a card,
    // so it reloads too, but at most every LIVE_RELOAD_MIN_MS.
    onItemsChanged: function (items) {
      if (LiveUpdates.shownOn(this.ui.body, items).length) {
        this.reloadInPlace();
        return;
      }
      if (!LiveUpdates.concerns(items, LIVE_INBOX)) { return; }
      var wait = (this.lastLiveReload || 0) + LIVE_RELOAD_MIN_MS - Date.now();
      if (wait <= 0) {
        this.reloadInPlace();
      } else if (!this.liveReloadTimer) {
        this.liveReloadTimer = window.setTimeout(_.bind(function () {
          this.liveReloadTimer = null;
          if (!this.isDestroyed) { this.reloadInPlace(); }
        }, this), wait);
      }
    },

    scrollState: function () {
      return { win: $(window).scrollTop(), body: this.ui.body.scrollTop() };
    },

    restoreScroll: function (state) {
      this.ui.body.scrollTop(state.body);
      $(window).scrollTop(state.win);
    },

    // The server drops its cached board when inbox items change, so a plain
    // read is fresh. The cards already loaded are reloaded (server cap 200).
    reloadInPlace: function () {
      var self = this;
      this.lastLiveReload = Date.now();
      var params = this.params();
      params.first = 0;
      params.pageSize = Math.min(200, Math.max(PAGE_SIZE, this.blocks.length));
      if (this.request && this.request.abort) { this.request.abort(); }
      this.request = App.request('salesInbox:support-worklist', params);
      this.request.done(function (page) {
        if (self.isDestroyed) { return; }
        var scroll = self.scrollState();
        self.blocks = page.blocks || [];
        self.total = page.total || 0;
        self.counts = page;
        self.renderBody();
        self.updateTabCounts();
        self.restoreScroll(scroll);
      });
    },

    params: function () {
      var params = {
        first: this.blocks.length,
        pageSize: PAGE_SIZE,
        sort: this.filters.sort
      };
      if (this.filters.kind) { params.kinds = this.filters.kind; }
      // A type filter narrows the board to the cards with an email of that
      // type (and shows only those emails on them).
      var cases = typeCases(this.filters.type);
      if (cases.length) { params.emailCases = cases.join(','); }
      if (this.filters.search) { params.search = this.filters.search; }
      // Resolved mails only come with a search (the server looks them up).
      if (this.filters.openScope === 'all') { params.includeResolved = true; }
      if (this.filters.openScope === 'extended') { params.searchMode = 'extended'; }
      // The server keeps the board's sources ~30 s; the refresh button asks
      // for a rebuild.
      if (this.forceRefresh) { params.refresh = true; }
      var me = currentUserSecId();
      var who = [];
      if ((this.filters.who === 'mine' || this.filters.who === 'mineAndUnassigned') && me) { who.push(me); }
      if (this.filters.who === 'unassigned' || this.filters.who === 'mineAndUnassigned') { who.push(UNASSIGNED); }
      if (who.length) { params.userSecIds = who.join(','); }
      return params;
    },

    fetch: function (append) {
      var self = this;
      if (!append) {
        this.blocks = [];
        this.ui.body.html('<div class="wl-state">Loading…</div>');
        this.ui.footer.empty();
      }
      if (this.request && this.request.abort) { this.request.abort(); }
      this.request = App.request('salesInbox:support-worklist', this.params());
      this.request.done(function (page) {
        if (self.isDestroyed) { return; }
        var added = page.blocks || [];
        self.blocks = self.blocks.concat(added);
        self.total = page.total || 0;
        self.counts = page;
        if (append) {
          var $added = $(_.map(added, self.blockHtml, self).join(''));
          self.ui.body.append($added);
          self.mountUsers($added);
          self.renderFooter();
        } else {
          self.renderBody();
        }
        self.updateTabCounts();
      }).fail(function (xhr) {
        if (self.isDestroyed || (xhr && xhr.statusText === 'abort')) { return; }
        self.ui.body.html('<div class="wl-state wl-state-bad">' +
          'Could not load the support worklist. Refresh to try again.</div>');
        self.ui.footer.empty();
      });
    },

    renderBody: function () {
      if (!this.blocks.length) {
        var filtered = !!(this.filters.kind || this.filters.search || this.filters.who || this.filters.type);
        this.ui.body.html('<div class="wl-state">' +
          '<span class="wl-state-icon">' + (filtered ? '🔍' : '🎉') + '</span>' +
          '<span class="wl-state-t">' + (filtered ? 'Nothing matches' : 'All clear') + '</span>' +
          '<span class="wl-state-s">' + (filtered ?
            'No order has that kind of work waiting.' :
            'No supplier email, delay or status is waiting on support.') + '</span></div>');
        this.ui.footer.empty();
        return;
      }
      this.destroyUsers();
      this.itemsByKey = {};
      this.ui.body.html(_.map(this.blocks, this.blockHtml, this).join(''));
      this.mountUsers(this.ui.body);
      this.renderFooter();
    },

    // ---- avatars (same component and look as the sales worklist) -----------
    mountUsers: function ($scope) {
      var self = this;
      var $rows = $scope.find('.wl-users');
      if (!$rows.length) { return; }
      require(['usersView', 'backbone'], function (UsersView, Backbone) {
        if (self.isDestroyed) { return; }
        $rows.each(function () {
          var $host = $(this);
          var item = self.itemsByKey[$host.attr('data-row')];
          if (!item) { return; }
          var users = item.userpids || [];
          self.mountUserGroup($host.find('.js-users-own'), UsersView, Backbone,
            _.where(users, { customerLinked: false }));
          self.mountUserGroup($host.find('.js-users-cust'), UsersView, Backbone,
            _.where(users, { customerLinked: true }));
        });
      });
    },

    mountUserGroup: function ($host, UsersView, Backbone, users) {
      if (!$host.length || !users.length) { return; }
      var view = new UsersView({
        model: new Backbone.Model({ userpids: users }),
        readOnly: true
      });
      view.render();
      $host.append(view.el);
      // UsersView builds its collection in onShow, which nothing triggers for a
      // view mounted by hand.
      view.triggerMethod('show');
      this.userViews.push(view);
    },

    destroyUsers: function () {
      _.each(this.userViews, function (view) { view.destroy(); });
      this.userViews = [];
    },

    renderFooter: function () {
      var more = this.blocks.length < this.total ?
        '<button type="button" class="js-more wl-more">Show more</button>' : '';
      this.ui.footer.html('<span class="wl-total"><b>' + this.blocks.length + '</b> of <b>' +
        this.total + '</b> orders</span>' + more);
    },

    updateTabCounts: function () {
      var counts = this.counts || {};
      this.$('.js-kind').each(function () {
        var key = $(this).attr('data-count');
        // "All" counts the cards of the unfiltered board: the per-kind numbers
        // overlap (a late order can also have mail), so no sum would be right.
        var n = key === 'total' ? null : counts[key];
        $(this).find('.n').text(n ? n : '');
      });
      this.$('.js-kind.on[data-count="total"] .n').text(this.total || '');
    },

    blockHtml: function (block) {
      var rows = _.map(block.items || [], function (item) { return this.rowHtml(item, block); }, this).join('');
      var linked = !!block.purchaseOrderId;
      var name = linked ?
        (block.offer ? 'Offer ' : '') + block.purchaseOrderId + (block.productName ? ' · ' + block.productName : '') +
          (block.quantity ? ' × ' + block.quantity : '') :
        (block.productName || 'Email not linked to an order');
      var sub = linked ?
        _.compact([block.customerName, block.supplierName ? 'supplier ' + block.supplierName : null,
          block.supplierNumber ? 'ref. ' + block.supplierNumber : null, block.statusName]).join(' · ') :
        _.compact([block.customerName, 'no order found — open the email to link one']).join(' · ');
      var wait = waitPill(block);
      var ship = shipPill(block);
      return BLOCK_TPL({
        blockKey: _.escape(block.blockKey),
        poi: _.escape(block.purchaseOrderId || ''),
        headTitle: linked ? 'Open the order' : 'Open the email',
        avTone: block.proofPending ? 'c0' : block.late ? 'c3' : linked ? 'c2' : 'c4',
        avIcon: linked ? 'fa-cube' : 'fa-envelope-o',
        name: _.escape(name),
        sub: _.escape(sub),
        waitTone: wait.tone,
        waitText: _.escape(wait.text),
        waitTitle: _.escape(wait.title),
        shipTone: ship.tone === 'none' ? 'ok' : ship.tone,
        shipText: _.escape(ship.text),
        shipTitle: _.escape(ship.title),
        proof: !!block.proofPending,
        late: !!block.late,
        daysLate: block.daysLate || 0,
        statusAction: !!block.statusAction,
        needCheck: !!block.needCheck,
        statusName: _.escape(block.statusName || 'status'),
        emailCount: block.emailCount || 0,
        priority: block.priorityScore || 0,
        fill: gaugeFill(block.priorityScore),
        heat: gaugeHeat(block.priorityScore || 0),
        priorityReason: _.escape(block.priorityReason || 'No boost'),
        rows: rows
      });
    },

    // Key a row for the avatar mount step, which reads the users back by it.
    registerRow: function (item, block) {
      var key = _.escape(block.blockKey + ':' + item.type + ':' + (item.secId || item.statusCode || '') +
        ':' + _.size(this.itemsByKey));
      this.itemsByKey[key] = item;
      return key;
    },

    rowHtml: function (item, block) {
      var isEmail = item.type === 'EMAIL';
      var icon = isEmail ? (item.proof ? 'fa-picture-o' : 'fa-envelope-o') :
        item.type === 'LATE' ? 'fa-truck' : item.type === 'CHECK' ? 'fa-clock-o' : 'fa-flag-o';
      var icTone = isEmail ? (item.proof ? '' : 'graph') : 'both';
      var icTitle = isEmail ? (item.proof ? 'Supplier proof to approve' : 'Support email') :
        item.type === 'LATE' ? 'Late' : item.type === 'CHECK' ? 'Need check' : 'Status waiting on support';
      var klass = isEmail ? prettyCase(item.emailCase || item.category) : null;
      // The avatars say who is on it; "nobody" only when nobody is.
      var meta = isEmail ?
        _.compact([item.senderEmail, (item.userpids || []).length ? null : 'nobody']).join(' · ') :
        item.type === 'LATE' ? item.daysLate + ' day(s) late' :
        item.type === 'CHECK' ? 'due since ' + (item.dueAt ? new Date(item.dueAt).toLocaleString('fr-BE') : '') +
          (item.checkedBefore ? ' (checked before)' : '') : '';
      var code = item.inboxStatus ? String(item.inboxStatus).toUpperCase() : null;
      var due = isEmail ? dueTag(item.replyDueAt) : null;
      return ROW_TPL({
        due: due ? _.escape(due.text) : '',
        dueOver: !!(due && due.over),
        type: item.type,
        secId: _.escape(item.secId || ''),
        poi: _.escape(block.purchaseOrderId || ''),
        icon: icon,
        icTone: icTone,
        icTitle: icTitle,
        title: _.escape(item.title || ''),
        summary: _.escape(item.summary || ''),
        note: _.escape(item.note || ''),
        status: code ? (STATUS_LABEL[code] || prettyCase(code)) : null,
        statusChip: isEmail ? RowActions.statusChip(item) : '',
        assignButtons: isEmail ? RowActions.assignButtons(item) : '',
        statusTone: code ? 'st-' + code.toLowerCase() : '',
        klass: klass ? _.escape(klass) : null,
        klassTone: CATEGORY_TONE[item.category] || 'idle',
        klassTitle: _.escape(_.compact([prettyCase(item.category), prettyCase(item.emailCase)]).join(' · ')),
        meta: _.escape(meta),
        // Need check: the status delay counts again from now. Late: hidden 24 h.
        checkable: (item.type === 'CHECK' || item.type === 'LATE') && !!block.purchaseOrderId,
        checkClass: item.type === 'LATE' ? 'js-late-checked' : 'js-checked',
        checkTitle: item.type === 'LATE' ?
          'Checked — hide this delay for 24 h (it comes back if there is still no tracking)' :
          'Checked — the delay counts again from now',
        rowKey: this.registerRow(item, block),
        ownTitle: isEmail ? 'Assigned to this email' : 'Assigned to the order',
        age: ageLabel(item.date),
        dateAttr: item.date ? new Date(item.date).toLocaleString('fr-BE') : ''
      });
    },

    // ---- filters ----------------------------------------------------------
    onKind: function (e) {
      this.filters.kind = $(e.currentTarget).attr('data-kind') || '';
      this.$('.js-kind').removeClass('on');
      $(e.currentTarget).addClass('on');
      this.changed();
    },

    onSort: function (e) {
      this.filters.sort = nextSort($(e.currentTarget).attr('data-sort'), this.filters.sort);
      renderSortTabs(this.$('.js-sort'), this.filters.sort);
      this.changed();
    },

    onWho: function (e) {
      this.filters.who = $(e.currentTarget).val() || '';
      this.changed();
    },

    onOpenScope: function (e) {
      this.filters.openScope = openScopeOf($(e.currentTarget).attr('data-open'));
      this.$('.js-open-scope').removeClass('on');
      $(e.currentTarget).addClass('on');
      // Without a search, All shows the same board: no need to refetch.
      saveFilters(this.filters);
      if (this.filters.search) { this.fetch(false); }
    },

    onType: function (e) {
      this.filters.type = $(e.currentTarget).val() || '';
      this.changed();
    },

    onSearch: function () {
      var self = this;
      if (this.searchTimer) { window.clearTimeout(this.searchTimer); }
      this.searchTimer = window.setTimeout(function () {
        self.filters.search = $.trim(self.ui.search.val());
        self.changed();
      }, 300);
    },

    onRefresh: function () {
      this.forceRefresh = true;
      this.fetch(false);
      this.forceRefresh = false;
    },

    // ---- row actions (status / assignee of an email) ---------------------
    // The buttons sit inside the row, whose click opens the email: keep the
    // click to themselves. The board is refetched once the change is saved.
    onRowStatus: function (e) {
      e.preventDefault();
      e.stopPropagation();
      RowActions.openStatusMenu($(e.currentTarget), _.bind(this.afterRowAction, this));
    },

    onRowAssignMe: function (e) {
      e.preventDefault();
      e.stopPropagation();
      $(e.currentTarget).prop('disabled', true);
      RowActions.assignMe($(e.currentTarget).attr('data-secid'), _.bind(this.afterRowAction, this));
    },

    onRowAssign: function (e) {
      e.preventDefault();
      e.stopPropagation();
      RowActions.openAssignee($(e.currentTarget), _.bind(this.afterRowAction, this));
    },

    afterRowAction: function () {
      if (this.isDestroyed) { return; }
      // The server keeps the board ~30 s: ask for a rebuild.
      this.onRefresh();
    },

    onMore: function () {
      this.fetch(true);
    },

    changed: function () {
      saveFilters(this.filters);
      this.fetch(false);
    },

    // "Checked": the status delay of that order counts again from now, so its
    // Need check item (and the clock on the board) goes away.
    // "Checked" on a Late item: hidden for 24 h.
    onLateChecked: function (e) {
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      var $btn = $(e.currentTarget).prop('disabled', true);
      var poi = $btn.closest('.wl-row').attr('data-poi');
      if (!poi) { return; }
      App.request('salesInbox:late-checked', poi).done(function () {
        if (self.isDestroyed) { return; }
        self.onRefresh();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        $btn.prop('disabled', false);
      });
    },

    onChecked: function (e) {
      e.preventDefault();
      e.stopPropagation();
      var self = this;
      var $btn = $(e.currentTarget).prop('disabled', true);
      var poi = $btn.closest('.wl-row').attr('data-poi');
      if (!poi) { return; }
      App.request('salesInbox:reset-check', poi).done(function () {
        if (self.isDestroyed) { return; }
        self.onRefresh();
      }).fail(function () {
        if (self.isDestroyed) { return; }
        $btn.prop('disabled', false);
      });
    },

    // ---- navigation -------------------------------------------------------
    onRowKey: function (e) {
      if (e.keyCode !== 13 && e.keyCode !== 32) { return; }
      e.preventDefault();
      this.onRowClick(e);
    },

    onHeadKey: function (e) {
      if (e.keyCode !== 13 && e.keyCode !== 32) { return; }
      e.preventDefault();
      this.onHeadClick(e);
    },

    // An email opens in its own window; a delay or a status is about the
    // order, so it opens the order.
    onRowClick: function (e) {
      e.stopPropagation();
      var $row = $(e.currentTarget);
      if ($row.attr('data-type') === 'EMAIL') {
        var secId = $row.attr('data-secid');
        var title = $row.find('.wl-ti-t').text();
        if (secId) { this.openEmail(secId, title); }
        return;
      }
      this.openOrder($row.attr('data-poi'));
    },

    onHeadClick: function (e) {
      var $card = $(e.currentTarget).closest('.wl-card');
      var poi = $card.attr('data-poi');
      if (poi) {
        this.openOrder(poi);
        return;
      }
      // A card without an order is one email: open it.
      var $row = $card.find('.wl-row[data-type="EMAIL"]').first();
      if ($row.length) { this.openEmail($row.attr('data-secid'), $row.find('.wl-ti-t').text()); }
    },

    openOrder: function (poi) {
      if (!poi) { return; }
      // attr() above, not data(): jQuery would coerce a numeric poi and eat
      // its leading zeros.
      require(['ordersDetailView'], function (DetailView) {
        DetailView.prototype.open(poi);
      });
    },

    openEmail: function (secId, title) {
      require(['salesInboxDetailView'], function (DetailView) {
        DetailView.prototype.open(secId, title);
      });
    },

    onDestroy: function () {
      this.destroyUsers();
      if (this.searchTimer) { window.clearTimeout(this.searchTimer); }
      if (this.liveReloadTimer) { window.clearTimeout(this.liveReloadTimer); }
      if (this.request && this.request.abort) { this.request.abort(); }
    }
  });
});

